import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api, apiError } from "@/lib/api";

export default function ReservationConfirmation() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const presetAnswer = params.get("answer");
  const [result, setResult] = useState(null); // "yes" | "no"
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const answer = async (value) => {
    if (!token) { setError("Lien invalide."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/public/booking/confirm", { token, answer: value });
      setResult(data.status === "confirmed" || data.status === "enrolled" || data.answer === "yes" ? "yes" : "no");
    } catch (e) { setError(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (presetAnswer === "yes" || presetAnswer === "no") answer(presetAnswer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] px-6 py-20">
      <div className="mx-auto max-w-lg rounded-2xl border border-neutral-200 bg-white p-8 sm:p-12">
        <Logo className="h-14" />
        {error && <p className="mt-8 text-red-600" data-testid="resa-confirm-error">{error}</p>}
        {busy && !result && <p className="mt-8 flex items-center gap-2 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /> Traitement…</p>}
        {result === "yes" && (
          <div className="mt-8" data-testid="resa-confirm-yes">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <h1 className="mt-5 text-3xl font-extrabold tracking-tight">Inscription confirmée !</h1>
            <p className="mt-4 text-neutral-600">Pense à contacter ton auto-école sous 2 jours pour finaliser ton inscription. Bonne route&nbsp;!</p>
          </div>
        )}
        {result === "no" && (
          <div className="mt-8" data-testid="resa-confirm-no">
            <XCircle className="h-10 w-10 text-neutral-400" />
            <h1 className="mt-5 text-3xl font-extrabold tracking-tight">Place libérée</h1>
            <p className="mt-4 text-neutral-600">Pas de souci, ta place est libérée. Tu peux <Link to="/reserver" className="font-semibold text-brand">réserver à nouveau</Link> quand tu veux.</p>
          </div>
        )}
        {!busy && !result && !error && (
          <div className="mt-8" data-testid="resa-confirm-ask">
            <h1 className="text-2xl font-extrabold tracking-tight">Confirmes-tu ton inscription ?</h1>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button onClick={() => answer("yes")} data-testid="resa-yes" className="flex-1 rounded-full bg-brand px-6 py-4 font-bold text-white hover:bg-brand-hover">Oui, je m'inscris</button>
              <button onClick={() => answer("no")} data-testid="resa-no" className="flex-1 rounded-full border border-neutral-300 px-6 py-4 font-bold text-neutral-700 hover:border-neutral-900">Non, j'annule</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
